import type { Address, Hex } from 'viem'
import type { ShieldedWalletClient } from 'seismic-viem'
import { SHIELDED_EXEC_GAS_BUFFER, SHIELDED_EXEC_GAS_FLOOR } from './constants'

type EstimateParams = {
  safeAddress: Address
  /** The owner/signer the call must run as, so `msg.sender` survives via the signed read. */
  from: Address
  /** Encoded `execTransaction` calldata (from `getEncodedSafeTx`). */
  data: Hex
  /** Safe transaction gas accounting, used to size the supplied gas limit. */
  safeTxGas?: bigint
  baseGas?: bigint
}

/**
 * Estimates gas for a Safe `execTransaction` on a shielded chain.
 *
 * A plain `eth_call`/`eth_estimateGas` zeroes `from` on Seismic, so it would revert with GS025. We
 * instead run a **signed call** (`signedCall`), which preserves `msg.sender`, to confirm the
 * transaction does not revert. seismic-viem exposes no signed `estimateGas`, so once the simulation
 * succeeds we return a safe over-estimate (unused gas is refunded). A revert here surfaces the real
 * reason (e.g. an actual GS-code) rather than the misleading zeroed-`from` GS025.
 */
export const estimateShieldedExecGas = async (
  client: ShieldedWalletClient,
  { safeAddress, from, data, safeTxGas = 0n, baseGas = 0n }: EstimateParams,
): Promise<bigint> => {
  await client.signedCall({ account: from, to: safeAddress, data })

  const accounted = baseGas + safeTxGas + SHIELDED_EXEC_GAS_BUFFER
  return accounted > SHIELDED_EXEC_GAS_FLOOR ? accounted : SHIELDED_EXEC_GAS_FLOOR
}

/** In-flight `estimateShieldedExecGas` promises, keyed by the signed-read request. */
const inFlightEstimates = new Map<string, Promise<bigint>>()

/**
 * Dedupes concurrent shielded estimates for the same request. Several hooks can mount on one screen
 * and each calls {@link estimateShieldedExecGas} (e.g. `useGasLimit` plus `useGasTooHigh`); without
 * this they would each fire a `signedCall` and the wallet would prompt multiple times. Callers with
 * an identical `{ from, safeAddress, data }` share a single in-flight signed read.
 */
export const estimateShieldedExecGasDeduped = (
  client: ShieldedWalletClient,
  params: EstimateParams,
): Promise<bigint> => {
  const key = `${params.from}:${params.safeAddress}:${params.data}`

  const existing = inFlightEstimates.get(key)
  if (existing) return existing

  const promise = estimateShieldedExecGas(client, params).finally(() => {
    inFlightEstimates.delete(key)
  })
  inFlightEstimates.set(key, promise)
  return promise
}
